import React, { useCallback } from "react";
import { InviteCode } from "@common/types/inviteCode";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import { withCurrentOrigin } from "@lib/currentOriginLink";
import { useCanCancelInvite } from "@hooks/permissions/useCanCancelInvite";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import {
    useCancelInviteMutation,
    useResendInviteMutation,
} from "@api/workspaceMember.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";

interface Props {
    invite: InviteCode;
}

export const InviteActions: React.FC<Props> = ({ invite }) => {
    const activeWorkspace = useActiveWorkspace();
    const { toast, error: errorToast } = useToaster();

    const canPerformCancelInvite = useCanPerformWorkspaceAction(
        WorkspaceAction.MEMBERS_EDIT,
    );
    const canCancelInvite =
        useCanCancelInvite(invite) && canPerformCancelInvite;
    const canResendInvite = useCanPerformWorkspaceAction(
        WorkspaceAction.MEMBERS_EDIT,
    );
    const canManage = canCancelInvite || canResendInvite;
    const resendInvite = useResendInviteMutation(activeWorkspace!);
    const cancelInvite = useCancelInviteMutation(activeWorkspace!);

    const copyToClipboard = useCopyToClipboard({
        successMessage: "Invite link copied to clipboard",
    });

    const handleCopyInviteLink = useCallback(() => {
        copyToClipboard(withCurrentOrigin(invite.inviteLink));
    }, [copyToClipboard, invite.inviteLink]);

    const handleCancel = useCallback(async () => {
        try {
            await cancelInvite.mutateAsync(invite);
            toast("Invite canceled");
        } catch {
            errorToast("Failed to cancel invite");
        }
    }, [cancelInvite, invite, toast, errorToast]);

    const handleResend = useCallback(async () => {
        try {
            await resendInvite.mutateAsync(invite);
            toast("Invite resent");
        } catch {
            errorToast("Failed to resend invite");
        }
    }, [invite, resendInvite, toast, errorToast]);

    if (!canManage) {
        return null;
    }

    return (
        <Menu.More>
            <Menu.Item onClick={handleCopyInviteLink}>
                Copy invite link
            </Menu.Item>

            {canResendInvite && (
                <Menu.Item onClick={() => handleResend()}>
                    Resend invite
                </Menu.Item>
            )}

            {canCancelInvite && (
                <Menu.Item destructive onClick={() => handleCancel()}>
                    Cancel invite
                </Menu.Item>
            )}
        </Menu.More>
    );
};
